import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import type { Page } from '@hellenica/shared';

export default function PageEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: page, isLoading } = useQuery<Page>({
    queryKey: ['page', id],
    queryFn: () => api.get(`/v1/admin/pages/${id}`),
    enabled: !!id,
  });

  const { register, handleSubmit } = useForm({
    values: page || { title: '', content: '' },
  });

  const saveMutation = useMutation({
    mutationFn: (data: Partial<Page>) => api.put(`/v1/admin/pages/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      toast.success('Page updated');
      navigate('/pages');
    },
  });

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">Edit Page: {page?.title}</h1>
      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input type="text" {...register('title')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Subtitle</label>
            <input type="text" {...register('subtitle')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Content</label>
            <textarea {...register('content')} rows={12} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate('/pages')} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-brand-bronze text-white rounded-md">Save</button>
        </div>
      </form>
    </div>
  );
}
